import type { CallbackDataParams, TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { EdgelessObject, normalizeResultScale, type IFramework, type IResultOptions } from '@/framework';
import { getCenterPointForOrigin } from '@/utils/layer-kit';
import { rotatePointWithFocus } from '@/utils/math-kit';
import { getFontFamilyWithFallback } from '@/utils/text-kit';
import { LayerType, type IChartLayer, type IPoint } from '@/version-types';
import { prepareChartLayerDocumentFonts } from './font-measurement';
import { renderInteractiveLayerContentToCanvas, renderLayerContentToCanvas } from './render/render-to-canvas';

export interface ChartTooltipItem {
    color?: string;
    name: string;
    value: string;
}

export interface ChartTooltipInfo {
    x: number;
    y: number;
    title?: string;
    items: ChartTooltipItem[];
    style: {
        color?: string;
        fontFamily?: string;
        fontSize: number;
        markerSize: number;
        paddingX: number;
        paddingY: number;
    };
}

type InteractiveCanvasResult = Awaited<ReturnType<typeof renderInteractiveLayerContentToCanvas>>;
type ChartTooltipListener = (tooltipInfo: ChartTooltipInfo | null) => void;
type ChartActionTarget = { dataIndex: number; seriesIndex: number };

export class EdgelessChartLayer extends EdgelessObject {
    static type = LayerType.CHART;
    private disposeInteractiveCanvas: (() => void) | null = null;
    private interactiveChartInstance: InteractiveCanvasResult['chartInstance'] = null;
    private interactiveCanvasResource: HTMLCanvasElement | null = null;
    private pendingChartTooltipPoint: IPoint | null = null;
    private chartTooltipDispatchVersion = 0;
    private chartTooltipRenderVersion = 0;
    private chartTooltipFallbackFrame: number | null = null;
    private chartTooltipInfo: ChartTooltipInfo | null = null;
    private chartTooltipListeners = new Set<ChartTooltipListener>();
    private chartHighlightTargets: ChartActionTarget[] = [];
    private chartSelectedTargets: ChartActionTarget[] = [];
    private canvasEnterAnimationResource: HTMLCanvasElement | null = null;
    private canvasEnterAnimationResourcePromise: Promise<HTMLCanvasElement | null> | null = null;
    private canvasAnimationVersion = 0;
    private isCanvasEnterAnimationRunning = false;
    private shouldPlayCanvasEnterAnimation = false;
    private hasPlayedCanvasEnterAnimation = false;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    private getLayer() {
        return this.service.flatLayer.getLayer(this.layerId) as IChartLayer;
    }

    private stopCanvasAnimation() {
        this.canvasAnimationVersion += 1;
        this.isCanvasEnterAnimationRunning = false;
        this.clearChartTooltipFallback();
        this.emitChartTooltip(null);
        this.chartHighlightTargets = [];
        this.chartSelectedTargets = [];
        this.disposeInteractiveCanvas?.();
        this.disposeInteractiveCanvas = null;
        this.interactiveChartInstance = null;
        this.interactiveCanvasResource = null;
        this.pendingChartTooltipPoint = null;
        this.canvasEnterAnimationResource = null;
        this.canvasEnterAnimationResourcePromise = null;
    }

    private setInteractiveCanvasResult(result: InteractiveCanvasResult) {
        this.interactiveChartInstance = result.chartInstance;
        this.interactiveCanvasResource = result.canvas;
        this.disposeInteractiveCanvas = () => {
            result.dispose();
            if (this.interactiveCanvasResource === result.canvas) {
                this.interactiveChartInstance = null;
                this.interactiveCanvasResource = null;
            }
        };
    }

    private consumeCanvasEnterAnimation() {
        const shouldAnimate = this.shouldPlayCanvasEnterAnimation && !this.hasPlayedCanvasEnterAnimation;
        this.shouldPlayCanvasEnterAnimation = false;
        this.hasPlayedCanvasEnterAnimation = this.hasPlayedCanvasEnterAnimation || shouldAnimate;
        return shouldAnimate;
    }

    private clearChartTooltipFallback() {
        if (this.chartTooltipFallbackFrame === null) return;
        window.cancelAnimationFrame(this.chartTooltipFallbackFrame);
        this.chartTooltipFallbackFrame = null;
    }

    private emitChartTooltip(tooltipInfo: ChartTooltipInfo | null) {
        this.chartTooltipInfo = tooltipInfo;
        this.chartTooltipListeners.forEach((listener) => listener(tooltipInfo));
    }

    private getTooltipValue(param: CallbackDataParams) {
        const layer = this.getLayer();
        const encodeKey =
            layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut'
                ? 'value'
                : layer.data.type.includes('horizontal')
                  ? 'x'
                  : 'y';
        const encodeValue = param.encode?.[encodeKey];
        const dimensionIndex = Array.isArray(encodeValue) ? encodeValue[0] : encodeValue;
        const value =
            Array.isArray(param.value) && typeof dimensionIndex === 'number'
                ? param.value[dimensionIndex]
                : param.value;

        return value === null || value === undefined ? '' : `${value}`;
    }

    private getTooltipStyle() {
        const layer = this.getLayer();
        const { fontFamilyMap } = layer.__runtime ?? {};
        const textStyle = layer.data.categoryAxisLabel.textStyle;
        const fontSize = textStyle.fontSize;
        const sourceFontFamily = textStyle.fontFamily;
        const fontFamily = sourceFontFamily
            ? getFontFamilyWithFallback(sourceFontFamily, fontFamilyMap?.[sourceFontFamily])
            : undefined;

        return {
            color: textStyle.color,
            fontFamily,
            fontSize,
            markerSize: Math.max(4, fontSize * 0.4),
            paddingX: fontSize * 0.8,
            paddingY: fontSize * 0.5,
        };
    }

    private getChartTooltipInfo(params: TopLevelFormatterParams): ChartTooltipInfo | null {
        const point = this.pendingChartTooltipPoint;
        if (!point) return null;

        const isAxisTooltip = Array.isArray(params);
        const paramsList = (isAxisTooltip ? params : [params]).filter((param): param is CallbackDataParams => !!param);
        if (!paramsList.length) return null;

        const layer = this.getLayer();
        const isPieChart = layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut';
        const firstParam = paramsList[0];

        return {
            x: point.x,
            y: point.y,
            title: isPieChart ? undefined : isAxisTooltip ? firstParam.name : firstParam.seriesName,
            items: paramsList.map((param) => ({
                color: typeof param.color === 'string' ? param.color : undefined,
                name: isAxisTooltip ? param.seriesName || param.name : param.name,
                value: this.getTooltipValue(param),
            })),
            style: this.getTooltipStyle(),
        };
    }

    private handleChartTooltipChange(params: TopLevelFormatterParams) {
        const tooltipInfo = this.getChartTooltipInfo(params);
        if (!tooltipInfo) return;

        this.syncChartHighlight(params);
        this.chartTooltipRenderVersion = this.chartTooltipDispatchVersion;
        this.emitChartTooltip(tooltipInfo);
    }

    private getChartActionTargets(params: TopLevelFormatterParams): ChartActionTarget[] {
        const paramsList = (Array.isArray(params) ? params : [params]).filter(
            (param): param is CallbackDataParams => !!param,
        );

        return paramsList.reduce<ChartActionTarget[]>((targets, param) => {
            if (typeof param.seriesIndex === 'number' && typeof param.dataIndex === 'number') {
                targets.push({
                    seriesIndex: param.seriesIndex,
                    dataIndex: param.dataIndex,
                });
            }
            return targets;
        }, []);
    }

    private getChartActionTargetKey(target: ChartActionTarget) {
        return `${target.seriesIndex}:${target.dataIndex}`;
    }

    private findChartActionTargetIndex(targets: ChartActionTarget[], target: ChartActionTarget) {
        const targetKey = this.getChartActionTargetKey(target);
        return targets.findIndex((item) => this.getChartActionTargetKey(item) === targetKey);
    }

    private getChartSelectionGroupTargets(target: ChartActionTarget) {
        const layer = this.getLayer();
        const isPieChart = layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut';
        if (isPieChart) return [target];

        return layer.data.chartData.slice(1).map((_, dataIndex) => ({
            seriesIndex: target.seriesIndex,
            dataIndex,
        }));
    }

    private isChartSelectionExclusive() {
        const layer = this.getLayer();
        return layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut';
    }

    private isSameChartActionTargets(currentTargets: ChartActionTarget[], nextTargets: ChartActionTarget[]) {
        if (currentTargets.length !== nextTargets.length) return false;

        const currentKeys = new Set(currentTargets.map((target) => this.getChartActionTargetKey(target)));
        return nextTargets.every((target) => currentKeys.has(this.getChartActionTargetKey(target)));
    }

    private syncChartHighlight(params: TopLevelFormatterParams) {
        const chartInstance = this.interactiveChartInstance;
        if (!chartInstance) return;

        const nextTargets = this.getChartActionTargets(params);
        if (this.isSameChartActionTargets(this.chartHighlightTargets, nextTargets)) return;

        this.chartHighlightTargets.forEach((target) => {
            chartInstance.dispatchAction({
                type: 'downplay',
                ...target,
            });
        });
        nextTargets.forEach((target) => {
            chartInstance.dispatchAction({
                type: 'highlight',
                ...target,
            });
        });
        this.chartHighlightTargets = nextTargets;
    }

    private toggleChartGroup(nextTargets: ChartActionTarget[]) {
        const chartInstance = this.interactiveChartInstance;
        if (!chartInstance || !nextTargets.length) return false;

        const groupTargets = this.getChartSelectionGroupTargets(nextTargets[0]);
        if (!groupTargets.length) return false;

        const isGroupSelected = groupTargets.every(
            (target) => this.findChartActionTargetIndex(this.chartSelectedTargets, target) !== -1,
        );
        const shouldSelect = !isGroupSelected;

        groupTargets.forEach((target) => {
            const selectedIndex = this.findChartActionTargetIndex(this.chartSelectedTargets, target);
            const isSelected = selectedIndex !== -1;
            if (isSelected === shouldSelect) return;

            chartInstance.dispatchAction({
                type: shouldSelect ? 'select' : 'unselect',
                ...target,
            });
            if (shouldSelect) {
                this.chartSelectedTargets.push(target);
            } else {
                this.chartSelectedTargets.splice(selectedIndex, 1);
            }
        });
        return true;
    }

    private selectChartGroup(nextTargets: ChartActionTarget[]) {
        const chartInstance = this.interactiveChartInstance;
        if (!chartInstance || !nextTargets.length) return false;

        const groupTargets = this.getChartSelectionGroupTargets(nextTargets[0]);
        if (!groupTargets.length) return false;

        const isGroupSelected = groupTargets.every(
            (target) => this.findChartActionTargetIndex(this.chartSelectedTargets, target) !== -1,
        );
        if (isGroupSelected && this.chartSelectedTargets.length === groupTargets.length) {
            groupTargets.forEach((target) => {
                chartInstance.dispatchAction({
                    type: 'unselect',
                    ...target,
                });
            });
            this.chartSelectedTargets = [];
            return true;
        }

        this.chartSelectedTargets.forEach((target) => {
            chartInstance.dispatchAction({
                type: 'unselect',
                ...target,
            });
        });
        this.chartSelectedTargets = [];

        groupTargets.forEach((target) => {
            chartInstance.dispatchAction({
                type: 'select',
                ...target,
            });
        });
        this.chartSelectedTargets = groupTargets;
        return true;
    }

    private clearChartHighlight() {
        const chartInstance = this.interactiveChartInstance;
        if (!chartInstance || !this.chartHighlightTargets.length) {
            this.chartHighlightTargets = [];
            return;
        }

        this.chartHighlightTargets.forEach((target) => {
            chartInstance.dispatchAction({
                type: 'downplay',
                ...target,
            });
        });
        this.chartHighlightTargets = [];
    }

    private async prepareDocumentFonts(layer: IChartLayer) {
        return prepareChartLayerDocumentFonts(this.service, layer);
    }

    private async getResource(animate = false, options: { preserveRunningAnimation?: boolean } = {}) {
        const layer = this.getLayer();
        const scale = super.getResourceScale();

        if (!animate && options.preserveRunningAnimation && this.isCanvasEnterAnimationRunning) {
            return this.canvasEnterAnimationResource ?? this.canvasEnterAnimationResourcePromise ?? undefined;
        }

        await this.prepareDocumentFonts(layer);
        this.stopCanvasAnimation();

        if (!animate) {
            const result = await renderInteractiveLayerContentToCanvas(layer, scale, {
                onFrame: (canvas) => {
                    super.drawAiWatermark(canvas);
                    // 内部画布更新不会触发图层数据变更，需要同步刷新页面缓存。
                    this.service.edgelessCanvas.setDirty({ object: this });
                },
                onTooltipChange: (params) => this.handleChartTooltipChange(params),
            });
            this.setInteractiveCanvasResult(result);
            return result.canvas;
        }

        const version = this.canvasAnimationVersion;
        this.isCanvasEnterAnimationRunning = true;
        this.canvasEnterAnimationResourcePromise = renderInteractiveLayerContentToCanvas(layer, scale, {
            animation: true,
            onFrame: (canvas) => {
                if (version !== this.canvasAnimationVersion) return;
                super.drawAiWatermark(canvas);
                this.service.edgelessCanvas.setDirty({ object: this });
            },
            onEnd: () => {
                if (version !== this.canvasAnimationVersion) return;
                this.isCanvasEnterAnimationRunning = false;
                this.canvasEnterAnimationResourcePromise = null;
                this.service.edgelessCanvas.setDirty({ object: this });
            },
            onTooltipChange: (params) => this.handleChartTooltipChange(params),
        })
            .then((result) => {
                if (version !== this.canvasAnimationVersion) {
                    result.dispose();
                    return null;
                }

                this.setInteractiveCanvasResult(result);
                this.canvasEnterAnimationResource = result.canvas;
                return result.canvas;
            })
            .catch((error) => {
                if (version === this.canvasAnimationVersion) {
                    this.isCanvasEnterAnimationRunning = false;
                    this.canvasEnterAnimationResourcePromise = null;
                }
                throw error;
            });

        return this.canvasEnterAnimationResourcePromise;
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = this.getLayer();
        const renderBox = this.getBaseInfo();
        await this.prepareDocumentFonts(layer);
        const canvas = await renderLayerContentToCanvas(layer, false, normalizeResultScale(scale));
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { canvas, renderBox };
    }

    override async update(onlyUpdateBaseInfo: boolean, isAdd = false) {
        if (isAdd && !this.hasPlayedCanvasEnterAnimation) {
            this.shouldPlayCanvasEnterAnimation = true;
        }
        await super.update(onlyUpdateBaseInfo, isAdd);
    }

    onChartTooltipChange(listener: ChartTooltipListener) {
        this.chartTooltipListeners.add(listener);
        return () => {
            this.chartTooltipListeners.delete(listener);
        };
    }

    override async loadResource() {
        const canvas = await this.getResource(this.consumeCanvasEnterAnimation(), {
            preserveRunningAnimation: true,
        });
        if (!canvas) return canvas;
        await super.drawAiWatermarkAsync(canvas);
        return canvas;
    }

    hideChartTooltip() {
        this.clearChartTooltipFallback();
        this.pendingChartTooltipPoint = null;
        this.chartTooltipDispatchVersion += 1;
        this.chartTooltipRenderVersion = this.chartTooltipDispatchVersion;
        this.emitChartTooltip(null);
        this.clearChartHighlight();
        this.interactiveChartInstance?.dispatchAction({
            type: 'hideTip',
        });
    }

    dispatchChartSelectEvent(scenePoint: IPoint) {
        if (!this.interactiveCanvasResource || !this.interactiveChartInstance) return false;

        this.clearChartHighlight();
        this.dispatchChartHoverEvent('mousemove', scenePoint);
        return this.isChartSelectionExclusive()
            ? this.selectChartGroup(this.chartHighlightTargets)
            : this.toggleChartGroup(this.chartHighlightTargets);
    }

    dispatchChartHoverEvent(type: string, scenePoint: IPoint) {
        const chartInstance = this.interactiveChartInstance;
        if (!this.interactiveCanvasResource || !chartInstance) return;
        if (type !== 'mousemove') return;

        const layer = this.getLayer();
        // 图层保存的是页内坐标，命中计算前先扣除长图页面偏移。
        const framePoint = {
            x: scenePoint.x,
            y: scenePoint.y - this.service.transform.calcLayerOffsetTop(layer),
        };
        const point = rotatePointWithFocus(framePoint, getCenterPointForOrigin(layer), 360 - layer.rotate);
        const x = point.x - layer.left;
        const y = point.y - layer.top;

        if (x < 0 || y < 0 || x > layer.width || y > layer.height) {
            this.hideChartTooltip();
            return;
        }

        this.pendingChartTooltipPoint = { x, y };
        this.chartTooltipDispatchVersion += 1;
        const dispatchVersion = this.chartTooltipDispatchVersion;
        this.clearChartTooltipFallback();
        chartInstance.dispatchAction({
            type: 'showTip',
            x,
            y,
        });
        this.chartTooltipFallbackFrame = window.requestAnimationFrame(() => {
            this.chartTooltipFallbackFrame = null;
            if (this.chartTooltipRenderVersion === dispatchVersion) return;

            if (
                layer.data.type === 'line-basic' &&
                this.chartTooltipInfo &&
                chartInstance.containPixel({ gridIndex: 0 }, [x, y])
            ) {
                this.chartTooltipRenderVersion = dispatchVersion;
                this.emitChartTooltip({
                    ...this.chartTooltipInfo,
                    x,
                    y,
                });
                return;
            }

            this.hideChartTooltip();
        });
    }

    override dispose() {
        this.stopCanvasAnimation();
    }
}
